import { Fragment } from 'react';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectBasicInfoStatus,
  selectUserData,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  RequiredDocumentSection,
} from '@/types/document';

import {
  isCaymanUser,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

import { RequiredDocumentSelectDrawer } from '../drawers/RequiredDocumentSelectDrawer';

type ProofOfAddressProps = {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export function ProofOfAddress({ open, onOpen, onClose }: ProofOfAddressProps) {
  const { t } = useTranslate();

  const { user, requiredDocumentSections, documentByDocType } =
    useAppSelector(selectUserData);

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const isBasicInfoProcessed =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved ||
    basicInfoStatus === DocumentStatus.Rejected;
  const isCayman = isCaymanUser(user);

  useDrawerOpenTracker(open);

  const defaultStatus =
    (isExistingCustomer(user) || isRemediationCustomer(user)) &&
    user?.addressDetailed?.poaRequired === false
      ? DocumentStatus.Approved
      : DocumentStatus.Pending;

  const proofOfAddressSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfAddress,
  ) || {
    type: DocumentSection.ProofOfAddress,
    displayText: 'documentSection.Proof_of_Address.title',
    status: defaultStatus,
    docs: [],
  };

  const checkBtnDisableForEXC = (
    section: RequiredDocumentSection,
    isNotPendingAndNoDocs: boolean,
  ) => {
    if (user?.clientReviewed) {
      if (section.status === DocumentStatus.Approved) {
        return true;
      }
      /**
       * If the EXC has already signed/uploaded section docs then set it true,
       * otherwise set it false
       */
      return isNotPendingAndNoDocs;
    } else {
      return true;
    }
  };

  const isButtonDisabled = (section: RequiredDocumentSection) => {
    if (!user) return true;
    const isNotPendingAndNoDocs =
      section.status !== DocumentStatus.Pending && !section.docs.length;
    if (user && isExistingCustomer(user)) {
      return checkBtnDisableForEXC(section, isNotPendingAndNoDocs);
    }

    return isNotPendingAndNoDocs;
  };

  return (
    <Fragment key={DocumentSection.ProofOfAddress}>
      <DocumentActionItem
        title={t('documentSection.Proof_of_Address.title')}
        description={t('status.pending')}
        onClick={onOpen}
        status={proofOfAddressSection.status}
        enabledForAllStatuses
        disabled={
          (user && isExistingCustomer(user) && !user.clientReviewed) ||
          (!isBasicInfoProcessed && !isCayman)
        }
        disabledButton={isButtonDisabled(proofOfAddressSection)}
      />
      <RequiredDocumentSelectDrawer
        open={open}
        section={proofOfAddressSection}
        onClose={onClose}
        documentByDocType={documentByDocType}
      />
    </Fragment>
  );
}
